/**
 * `ky` instance construction. Composes the middleware hooks declared in
 * `middleware/` into a single configured client with retry-with-backoff
 * tuned for safe verbs.
 *
 * Why ky over hand-rolled fetch wrappers: idempotent retry on transient
 * network errors and 5xx is one config flag away, the hook lifecycle
 * (`beforeRequest` / `afterResponse` / `beforeRetry`) is exactly the shape
 * the auth-refresh + GPC + vendor-org middleware composes around, and
 * ~5kb is a fair price compared to writing those primitives ourselves.
 */
import ky, {
  type AfterResponseHook,
  type KyInstance,
  type Options as KyOptions,
} from 'ky';
import {
  acquireRefreshedToken,
  authBeforeRequest,
  authAfterResponse,
  type AuthRefreshConfig,
  type RefreshResult,
} from './middleware/auth-refresh';
import { gpcBeforeRequest } from './middleware/gpc';
import {
  vendorOrgBeforeRequest,
  type VendorOrgAccessor,
} from './middleware/vendor-org';

export type { AfterResponseHook } from 'ky';

export interface TransportConfig extends AuthRefreshConfig {
  /** API base URL — usually `window.__ENV?.NX_API_URL`. */
  baseURL?: string;
  /**
   * Vendor-org masquerade accessor (SMS opts in by passing `() => activeOrg.id`;
   * AIM/Beacon/One leave it undefined and the header is never sent).
   */
  vendorOrgAccessor?: VendorOrgAccessor;
  /**
   * Override ky's default retry. Defaults to ky's safe-verbs default (GET,
   * PUT, HEAD, DELETE, OPTIONS, TRACE) with status codes 408/413/500/502/
   * 503/504, exponential backoff, capped at 2 retries — POST is
   * deliberately excluded so re-creating side effects is opt-in.
   *
   * 429 is NOT retried. A 429 means the server is asking the
   * client to back off; client-side exponential retry amplifies the
   * throttle storm instead of damping it. The backend emits a
   * ``Retry-After`` header and the caller (React Query, etc.)
   * keeps its own retry semantics — propagate the 429 up rather than
   * eating it here.
   */
  retry?: KyOptions['retry'];
  /** Per-request timeout in ms. Default: 30s. */
  timeout?: number;
  /**
   * Extra ky options surfaced for tests / niche call-sites. Most consumers
   * should not need this — the lib's middleware composition is the public
   * surface.
   */
  kyExtensions?: KyOptions;
  /**
   * Optional app-supplied `afterResponse` hooks. Compose **after** the lib's
   * built-in auth-refresh hook (so when a 401 retries, the new response — not
   * the original 401 — is what these hooks see). Hooks should treat themselves
   * as fire-and-forget side effects: returning `undefined` lets the lib
   * propagate the response unchanged. Returning a `Response` will replace the
   * one upstream code sees, mirroring ky's native `afterResponse` contract;
   * this is rarely the right move for app-level hooks, which usually just
   * want to observe (e.g. SMS's `X-Vendor-Masquerade-Stale` recovery, which
   * fires `clearStaleMasquerade()` and returns nothing).
   *
   * AIM / Beacon / One leave this unset; the hook path is dormant for them.
   */
  afterResponse?: AfterResponseHook[];
}

/**
 * What `buildTransport` returns. The ky instance is the existing surface;
 * `refreshAccessToken` is exposed so apps can invoke a manual refresh
 * (userHandlers permissions-changed handler, Beacon TokenRestorer, etc.)
 * and have it collapse onto the same single-flight promise as the
 * auth-refresh middleware's auto-refresh.
 */
export interface BuildTransportResult {
  ky: KyInstance;
  /**
   * Run a manual refresh through the configured refresh path
   * (`refreshEndpoint` or `refresh` callback). Single-flight: concurrent
   * calls collapse onto one in-flight promise. Resolves with the new
   * `RefreshResult` (`{token: ...}`) — `token` is the new access token on
   * success, `null` on failure. Persists to storage and fires the
   * `onTokenRefreshComplete` listeners on success, identical to the
   * auto-refresh path.
   */
  refreshAccessToken: () => Promise<RefreshResult>;
}

const DEFAULT_TIMEOUT_MS = 30_000;

const DEFAULT_RETRY: KyOptions['retry'] = {
  limit: 2,
  methods: ['get', 'put', 'head', 'delete', 'options', 'trace'],
  // 429 omitted — see docstring on ``HttpClientConfig.retry``.
  statusCodes: [408, 413, 500, 502, 503, 504],
  backoffLimit: 8_000,
};

/**
 * Resolve a configured `refreshEndpoint` against the configured `baseURL`.
 * Absolute URLs (`http(s)://...`) pass through unchanged. Relative paths
 * (`/api/v2/api-token-refresh/`) are joined with `baseURL`. If `baseURL`
 * is empty (jsdom tests, dev `__ENV` not set), the path is left as-is so
 * `fetch` resolves it against `window.location.origin`.
 */
function resolveRefreshEndpoint(
  refreshEndpoint: string,
  baseURL: string | undefined,
): string {
  if (/^https?:\/\//i.test(refreshEndpoint)) {
    return refreshEndpoint;
  }
  if (!baseURL) return refreshEndpoint;
  // Trim trailing slash on baseURL and leading slash on path so we don't
  // produce double-slashes that some servers reject.
  const base = baseURL.replace(/\/+$/, '');
  const path = refreshEndpoint.replace(/^\/+/, '/');
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
}

/**
 * Build a ky instance with the auth-refresh + GPC + vendor-org middleware
 * composed in. The instance is what `createClient` hands to `openapi-fetch`
 * via its `fetch` option. The returned `refreshAccessToken` is wired to the
 * same single-flight refresh as the middleware.
 */
export function buildTransport(config: TransportConfig): BuildTransportResult {
  // Resolve `refreshEndpoint` against `baseURL` once, before the middleware
  // sees the config. The middleware itself only knows how to `fetch` an
  // absolute (or `window.location.origin`-relative) URL — keeping baseURL
  // resolution at this layer means the auth-refresh module stays unaware of
  // transport-level concerns.
  const resolvedConfig: TransportConfig =
    config.refreshEndpoint && !config.refresh
      ? {
          ...config,
          refreshEndpoint: resolveRefreshEndpoint(
            config.refreshEndpoint,
            config.baseURL,
          ),
        }
      : config;

  // The auth-refresh `afterResponse` hook needs to retry requests through
  // the same ky instance (so middleware re-fires). Resolve that cycle with
  // a deferred accessor — we capture `instance` after construction.
  let instance: KyInstance | null = null;
  const getKy = (): KyInstance => {
    if (!instance) {
      throw new Error(
        '[http-client] ky instance accessed before transport construction completed',
      );
    }
    return instance;
  };

  const baseHooks: KyOptions['hooks'] = {
    beforeRequest: [
      authBeforeRequest(resolvedConfig),
      gpcBeforeRequest(),
      ...(resolvedConfig.vendorOrgAccessor
        ? [vendorOrgBeforeRequest(resolvedConfig.vendorOrgAccessor)]
        : []),
    ],
    // App-supplied `afterResponse` hooks compose AFTER the lib's
    // auth-refresh hook so they observe the post-retry response (not the
    // original 401). The auth-refresh hook may swap the response via
    // `retryWithToken`; ky propagates that swapped response down the
    // afterResponse chain, so app hooks see what the call site will receive.
    afterResponse: [
      authAfterResponse(resolvedConfig, getKy),
      ...(resolvedConfig.afterResponse ?? []),
    ],
  };

  instance = ky.create({
    prefix: config.baseURL,
    timeout: config.timeout ?? DEFAULT_TIMEOUT_MS,
    retry: config.retry ?? DEFAULT_RETRY,
    hooks: baseHooks,
    ...config.kyExtensions,
  });

  return {
    ky: instance,
    refreshAccessToken: async () => {
      const token = await acquireRefreshedToken(resolvedConfig);
      return { token };
    },
  };
}
